import {
	type AppState,
	Auth0Provider,
	type User as Auth0User,
	type GetTokenSilentlyOptions,
	type LogoutOptions,
	type RedirectLoginOptions,
	useAuth0,
} from "@auth0/auth0-react";
import type { GetTokenSilentlyVerboseResponse } from "@auth0/auth0-spa-js";
import { useRouter } from "next/router";
import { type FC, type PropsWithChildren, useCallback, useEffect, useState } from "react";
import { CookiesProvider } from "react-cookie";

import { invokePostMessage, isRunningInIframe } from "@moment/ui";

import { authenticationActorRef } from "~/state/machines/authentication";

import { getAuthConfigs } from "./authconfig";
import { getAuthURLs, isDesktopAppMode } from "./interop/common";
import { onLoginSuccess } from "./loginAction";
import { deleteRedirectEntry } from "./redirectStore";

const AuthProviderComponent: FC<PropsWithChildren> = ({ children }) => {
	const router = useRouter();

	const authConfig = getAuthConfigs(process.env.AUTH_ENV);
	const { loginRedirectUri } = getAuthURLs(process.env.APP_ENV);

	const onRedirectCallback = useCallback(
		(appState: AppState | undefined) => {
			if (appState) {
				const returnTo = onLoginSuccess(appState["redirectId"]);
				if (returnTo) {
					if (isDesktopAppMode()) {
						router
							.replace({ query: {}, pathname: returnTo }, undefined, {
								shallow: true,
							})
							.catch((err) => console.error("Error redirecting after login", err));
						return;
					}

					// On the web platform because of the use of iframe,
					// it's best to do a manual navigation.
					window.location.href = `${window.location.protocol}//${window.location.host}${returnTo}`;
				}
			}
		},
		[router]
	);

	return (
		<Auth0Provider
			domain={authConfig.auth0Domain}
			clientId={authConfig.auth0ClientId}
			authorizationParams={{ redirect_uri: loginRedirectUri, audience: authConfig.audience }}
			onRedirectCallback={onRedirectCallback}
			cacheLocation="localstorage"
			useRefreshTokens
		>
			{children}

			<AccessTokenInjector />
		</Auth0Provider>
	);
};

export const AuthProvider: React.FC<PropsWithChildren> = ({ children }) => (
	<CookiesProvider>
		<AuthProviderComponent>{children}</AuthProviderComponent>
	</CookiesProvider>
);

export type GetAccessTokenSilently = (opts?: {
	promptForLogin?: boolean;
	redirectPath?: string;
}) => Promise<string | undefined>;

const iframeAuth0 = {
	loginWithRedirect: async (options?: RedirectLoginOptions<AppState>) => {
		// We don't need the redirect entry in the iframe.
		deleteRedirectEntry(options?.appState?.["redirectId"]);
		await invokePostMessage<void>("loginWithRedirect");
	},
	getAuth0User: async () => await invokePostMessage<Auth0User>("getAuth0User"),
	isAuthenticated: async () => await invokePostMessage<boolean>("isAuthenticated"),
	logout: async (options?: LogoutOptions) => {
		const returnTo = options?.logoutParams?.returnTo;
		if (returnTo) {
			const returnToUrl = new URL(returnTo);
			const redirectId = returnToUrl.searchParams.get("redirectId");
			if (redirectId) {
				// We don't need the redirect entry in the iframe.
				deleteRedirectEntry(redirectId);
			}
		}
		await invokePostMessage<void>("logoutWithRedirect");
	},
};

const AccessTokenInjector = () => {
	const { getAccessTokenSilently, isAuthenticated, loginWithRedirect, logout, user } = useAuth0();
	const [parentFrameIsAuthenticated, setParentFrameIsAuthenticated] = useState<boolean>(false);
	const [parentFrameUser, setParentFrameUser] = useState<Auth0User>();
	const { query } = useRouter();

	useEffect(() => {
		// We never want to run this hook for the desktop app.
		if (isDesktopAppMode()) {
			return;
		}

		// For the web platform, don't set the iframe
		// hooks in the auth actor if the app is not
		// not in an iframe.
		if (!isRunningInIframe()) {
			return;
		}

		if (!parentFrameIsAuthenticated) {
			iframeAuth0
				.isAuthenticated()
				.then(async (v: boolean | undefined) => {
					setParentFrameIsAuthenticated(v || false);
				})
				.catch(console.error);
		}

		if (!parentFrameUser) {
			iframeAuth0
				.getAuth0User()
				.then((u) => setParentFrameUser(u))
				.catch(console.error);
		}

		authenticationActorRef.send({
			type: "context.receivedAuth0StateUpdate",
			isAuthenticated: parentFrameIsAuthenticated,
			user: parentFrameUser,
			loginWithRedirect: iframeAuth0.loginWithRedirect,
			logout: iframeAuth0.logout,
			// Ignore TS error for not providing overloaded methods.
			// @ts-ignore
			getAccessTokenSilently: async (
				options?: GetTokenSilentlyOptions
			): Promise<GetTokenSilentlyVerboseResponse | string | undefined> => {
				const invokeOpts = {
					promptForLogin: false,
					redirectPath: window.location.pathname,
				};
				if (options?.detailedResponse) {
					return await invokePostMessage<GetTokenSilentlyVerboseResponse>(
						"getAccessTokenSilently",
						invokeOpts
					);
				}
				return await invokePostMessage<string>("getAccessTokenSilently", invokeOpts);
			},
		});
	}, [parentFrameIsAuthenticated, parentFrameUser, query]);

	useEffect(() => {
		// For the web platform, if the current DOM
		// is an iframe, don't send the auth actor
		// any of the auth0 hooks.
		if (!isDesktopAppMode() && isRunningInIframe()) {
			return;
		}

		// console.log("AUTHN", {
		// 	loginWithRedirect,
		// });
		authenticationActorRef.send({
			type: "context.receivedAuth0StateUpdate",
			isAuthenticated,
			user,
			loginWithRedirect,
			logout,
			getAccessTokenSilently,
		});
	}, [getAccessTokenSilently, isAuthenticated, loginWithRedirect, logout, user]);

	return null;
};
